import type { MessageDescriptor } from 'react-intl';
import { pyround } from 'src/utils/useful_functions';
import messages from 'src/messages';

export type CustomFieldKind =
  | 'text'
  | 'number'
  | 'choice'
  | 'list'
  | 'boolean';

/** A translatable message plus any values it interpolates. */
export type FieldMessage = {
  message: MessageDescriptor;
  values?: Record<string, any>;
};

export type FormatMessage = (
  descriptor: MessageDescriptor,
  values?: Record<string, any>
) => string;

export const MAX_PRECISION = 6;

export const FIELD_TYPES: Array<{
  kind: CustomFieldKind;
  label: MessageDescriptor;
}> = [
  { kind: 'text', label: messages.customFieldTypeText },
  { kind: 'number', label: messages.customFieldTypeNumber },
  { kind: 'choice', label: messages.customFieldTypeChoice },
  { kind: 'list', label: messages.customFieldTypeList },
  { kind: 'boolean', label: messages.customFieldTypeBoolean },
];

export const CATEGORIES: Array<{ key: string; label: MessageDescriptor }> = [
  { key: 'project', label: messages.customFieldsProject },
  { key: 'network_site', label: messages.customFieldsNetworkSite },
  { key: 'subscriber_site', label: messages.customFieldsSubscriberSite },
  { key: 'ptp_link', label: messages.customFieldsPtpLink },
  { key: 'ptp_end', label: messages.customFieldsPtpEnd },
  { key: 'access_point', label: messages.customFieldsAccessPoint },
  { key: 'subscriber', label: messages.customFieldsSubscriber },
];

/** The in-progress definition being edited by the modal. */
export type FieldDraft = {
  name: string;
  kind: CustomFieldKind;
  /** Default for text and choice fields, and the entered text for numbers. */
  defaultText: string;
  boolDefault: boolean;
  precision: number | string;
  minVal: string;
  maxVal: string;
  choices: string[];
  listDefault: string[];
};

/**
 * Every kind's settings panel takes the same props, so the modal can pick one
 * by kind instead of branching through them.
 */
export type KindSettingsProps = {
  draft: FieldDraft;
  /** Validation messages for the current kind (only numbers produce any). */
  errors: FieldMessage[];
  onChange: (patch: Partial<FieldDraft>) => void;
};

export function parseOptionalNumber(raw: string): number | null | undefined {
  const text = String(raw ?? '').trim();
  if (text === '') return null;
  const value = Number(text);
  if (!Number.isFinite(value)) return undefined;
  return value;
}

export function parseRequiredNumber(raw: string): number | undefined {
  const text = String(raw ?? '').trim();
  if (text === '') return 0;
  const value = Number(text);
  if (!Number.isFinite(value)) return undefined;
  return value;
}

/** Validate number-field definition inputs. Returns messages to display. */
export function validateNumberFieldDefinition({
  precision,
  minVal,
  maxVal,
  defaultText,
}: {
  precision: number | string;
  minVal: string;
  maxVal: string;
  defaultText: string;
}): FieldMessage[] {
  const errors: FieldMessage[] = [];

  const prec = Number(precision);
  if (!Number.isInteger(prec) || prec < 0 || prec > MAX_PRECISION) {
    errors.push({
      message: messages.customFieldPrecisionRange,
      values: { max: MAX_PRECISION },
    });
  }

  const min = parseOptionalNumber(minVal);
  const max = parseOptionalNumber(maxVal);
  if (min === undefined) {
    errors.push({ message: messages.customFieldMinimumInvalid });
  }
  if (max === undefined) {
    errors.push({ message: messages.customFieldMaximumInvalid });
  }
  if (min != null && max != null && min > max) {
    errors.push({ message: messages.customFieldMinimumAboveMaximum });
  }

  const defaultValue = parseRequiredNumber(defaultText);
  if (defaultValue === undefined) {
    errors.push({ message: messages.customFieldDefaultInvalid });
  } else {
    if (min != null && defaultValue < min) {
      errors.push({ message: messages.customFieldDefaultBelowMinimum });
    }
    if (max != null && defaultValue > max) {
      errors.push({ message: messages.customFieldDefaultAboveMaximum });
    }
  }

  return errors;
}

/**
 * Turn the edited draft into the payload the custom fields API expects.
 * Only the properties that apply to the chosen kind are sent.
 */
export function buildFieldPayload(draft: FieldDraft): any {
  const base = { name: draft.name.trim(), kind: draft.kind };

  switch (draft.kind) {
    case 'number': {
      const prec = Number(draft.precision);
      const min = parseOptionalNumber(draft.minVal);
      const max = parseOptionalNumber(draft.maxVal);
      const defaultValue = parseRequiredNumber(draft.defaultText) ?? 0;
      return {
        ...base,
        precision: prec,
        min: min == null ? null : pyround(min, prec),
        max: max == null ? null : pyround(max, prec),
        default: pyround(defaultValue, prec),
      };
    }
    case 'choice': {
      // A default typed in by hand becomes a choice in its own right
      const isNewChoice =
        draft.defaultText && !draft.choices.includes(draft.defaultText);
      return {
        ...base,
        choices: isNewChoice
          ? [draft.defaultText, ...draft.choices]
          : [...draft.choices],
        default: draft.defaultText,
      };
    }
    case 'list':
      return {
        ...base,
        choices: draft.choices,
        default: draft.listDefault.filter((v) => draft.choices.includes(v)),
      };
    case 'boolean':
      return { ...base, default: draft.boolDefault };
    case 'text':
    default:
      return { ...base, default: draft.defaultText };
  }
}

const isChoiceLike = (kind: string) => kind === 'choice' || kind === 'list';

/**
 * Warning to show about stored values, or null when nothing is at risk.
 * Changing the type discards everything; dropping choices only discards the
 * values that are no longer offered.
 */
export function describeValueReset(
  existingField: any | null,
  draft: FieldDraft
): MessageDescriptor | null {
  if (!existingField) return null;

  if (draft.kind !== existingField.kind) {
    return messages.customFieldTypeChangeWarning;
  }

  const choicesChanged =
    JSON.stringify(draft.choices) !==
    JSON.stringify(existingField.choices ?? []);
  if (isChoiceLike(draft.kind) && choicesChanged) {
    return messages.customFieldChoicesChangeWarning;
  }

  return null;
}

/**
 * Patch for a changed choice list. A choice that goes away can no longer be
 * part of a list field's default.
 */
export function choicesPatch(
  next: string[],
  draft: FieldDraft
): Partial<FieldDraft> {
  return {
    choices: next,
    listDefault: draft.listDefault.filter((v) => next.includes(v)),
  };
}

/** Display name of a field kind, e.g. "List (multi-select)". */
export function fieldKindLabel(kind: string): MessageDescriptor | null {
  return FIELD_TYPES.find((t) => t.kind === kind)?.label ?? null;
}

/** Human readable default value for the summary table. */
export function formatFieldDefault(
  field: any,
  formatMessage: FormatMessage
): string {
  if (Array.isArray(field.default)) {
    return field.default.join(', ');
  }
  if (field.kind === 'boolean') {
    return formatMessage(
      field.default ? messages.customFieldYes : messages.customFieldNo
    );
  }
  return String(field.default ?? '');
}
